import React from 'react';
import { SunIcon, MessageCircleIcon, FlameIcon, CheckIcon, CoffeeIcon, SparklesIcon, BarChartIcon, WrenchIcon, SaladIcon, CupSodaIcon, CarrotIcon, BeakerIcon, WavesIcon } from 'lucide-react';
interface HomeScreenProps {
  userName?: string;
  tasks: boolean[];
  toggleTask: (index: number) => void;
  openModal: (id: string) => void;
  navTo: (screen: string) => void;
}
export function HomeScreen({
  userName,
  tasks,
  toggleTask,
  openModal,
  navTo
}: HomeScreenProps) {
  const taskList = [{
    text: 'Morning Bitter Ritual (warm water + bitters)',
    time: '7 AM'
  }, {
    text: '3-Point Lymph Press technique',
    time: '9 AM'
  }, {
    text: 'First Drainage Meal (high fiber + sulfur)',
    time: '12 PM'
  }, {
    text: '5-minute lymph walk after lunch',
    time: '1 PM'
  }, {
    text: 'Evening magnesium drink before bed',
    time: '9 PM'
  }];
  return <div className="flex flex-col min-h-screen pb-24 animate-fade-in" data-id="element-73">
      {/* Hero */}
      <div className="bg-gradient-to-br from-[#2C4A30] via-[#4A7A50] to-[#7A9E7E] px-5 pt-6 pb-10 relative overflow-hidden" data-id="element-74">
        <div className="absolute -top-10 -right-10 w-[200px] h-[200px] bg-white/5 rounded-full" data-id="element-75"></div>
        <div className="absolute -bottom-16 -left-5 w-[160px] h-[160px] bg-white/5 rounded-full" data-id="element-76"></div>

        <div className="text-white/75 text-sm mb-1 flex items-center gap-1.5" data-id="element-77">
          Good morning, {userName || 'Sarah'}{' '}
          <SunIcon className="w-4 h-4 text-gold-light" data-id="element-78" />
        </div>
        <div className="text-white text-[26px] font-extrabold leading-tight mb-4" data-id="element-79">
          Your body is ready
          <br data-id="element-80" />
          to <span className="text-gold-light" data-id="element-81">flow again.</span>
        </div>

        <div className="bg-white/10 rounded-2xl p-4 backdrop-blur-md border border-white/15" data-id="element-82">
          <div className="flex justify-between items-center mb-3" data-id="element-83">
            <span className="text-white/80 text-xs font-semibold uppercase tracking-widest" data-id="element-84">
              7-Day Reset
            </span>
            <span className="text-gold-light text-sm font-bold" data-id="element-85">
              43% Complete
            </span>
          </div>
          <div className="bg-white/20 rounded-md h-1.5 overflow-hidden mb-3" data-id="element-86">
            <div className="h-full bg-gradient-to-r from-gold-light to-[#FFD700] rounded-md w-[43%] transition-all duration-1000" data-id="element-87"></div>
          </div>
          <div className="flex gap-1.5" data-id="element-88">
            {[0, 1, 2, 3, 4, 5, 6].map(i => <div key={i} className={`flex-1 h-2 rounded-full transition-colors duration-300 ${i < 2 ? 'bg-gold-light' : i === 2 ? 'bg-white animate-glow' : 'bg-white/20'}`} data-id="element-89"></div>)}
          </div>
        </div>
      </div>

      {/* Welcome Banner */}
      <div className="mx-5 mt-4 bg-gradient-to-br from-[#FFF8E8] to-[#FFF0D0] rounded-2xl p-4 flex items-center gap-3 border border-gold/20" data-id="element-90">
        <MessageCircleIcon className="w-8 h-8 text-gold shrink-0" data-id="element-91" />
        <div className="flex-1" data-id="element-92">
          <div className="text-[13px] italic text-text-mid leading-relaxed" data-id="element-93">
            "Your body doesn't need more input. It needs better output."
          </div>
          <div className="text-[11px] text-gold font-semibold mt-1" data-id="element-94">
            — The Drainage Code
          </div>
        </div>
      </div>

      {/* Streak */}
      <div className="mx-5 mt-3 bg-white rounded-2xl p-4 flex items-center gap-4 shadow-[0_2px_12px_rgba(0,0,0,0.06)]" data-id="element-95">
        <FlameIcon className="w-9 h-9 text-orange-500 shrink-0" data-id="element-96" />
        <div className="flex-1" data-id="element-97">
          <div className="text-[22px] font-extrabold text-text-dark leading-none mb-1" data-id="element-98">
            3 Days
          </div>
          <div className="text-xs text-text-light" data-id="element-99">Current streak</div>
        </div>
        <div className="bg-gradient-to-br from-gold to-[#E8A000] text-white text-[11px] font-bold px-2.5 py-1 rounded-full" data-id="element-100">
          Keep going!
        </div>
      </div>

      {/* Today Section */}
      <div className="px-5 pt-5 pb-2.5 flex items-center justify-between" data-id="element-101">
        <div className="text-lg font-bold text-text-dark" data-id="element-102">Today — Day 3</div>
        <div className="text-[13px] text-sage-dark font-semibold cursor-pointer" onClick={() => navTo('program')} data-id="element-103">
          View Plan →
        </div>
      </div>

      <div className="mx-5 mb-4 bg-gradient-to-br from-[#F0F7F1] to-[#E8F4EA] rounded-3xl p-5 border border-sage/20" data-id="element-104">
        <div className="inline-flex items-center gap-1.5 bg-sage-dark text-white text-[11px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full mb-3" data-id="element-105">
          <WavesIcon className="w-3.5 h-3.5" data-id="element-106" /> Day 3 · Activation Phase
        </div>
        <div className="text-xl font-extrabold text-text-dark mb-1.5" data-id="element-107">
          Drainage Activation
        </div>
        <div className="text-sm text-text-mid leading-relaxed mb-4" data-id="element-108">
          Your pipes are open. Today we introduce your first Drainage Meal and
          ramp up lymph movement. This is where the visible change begins.
        </div>

        <div className="flex flex-col gap-2.5" data-id="element-109">
          {taskList.map((task, i) => <div key={i} onClick={() => toggleTask(i)} className="flex items-center gap-3 bg-white rounded-xl p-3 cursor-pointer transition-transform active:scale-[0.98] shadow-[0_2px_8px_rgba(0,0,0,0.04)]" data-id="element-110">
              <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center shrink-0 transition-all duration-300 ${tasks[i] ? 'bg-sage-dark border-sage-dark text-white' : 'border-[#D0D0D0] text-transparent'}`} data-id="element-111">
                <CheckIcon className="w-4 h-4" data-id="element-112" />
              </div>
              <div className={`flex-1 text-sm font-medium transition-colors ${tasks[i] ? 'line-through text-text-light' : 'text-text-dark'}`} data-id="element-113">
                {task.text}
              </div>
              <div className="text-xs text-text-light whitespace-nowrap" data-id="element-114">
                {task.time}
              </div>
            </div>)}
        </div>
      </div>

      {/* Quick Access */}
      <div className="px-5 pt-2 pb-2.5" data-id="element-115">
        <div className="text-lg font-bold text-text-dark" data-id="element-116">Quick Access</div>
      </div>

      <div className="px-5 grid grid-cols-2 gap-3 mb-4" data-id="element-117">
        <div className="bg-white rounded-2xl p-4 cursor-pointer transition-transform active:scale-[0.97] shadow-[0_2px_12px_rgba(0,0,0,0.06)]" onClick={() => openModal('bitter')} data-id="element-118">
          <CoffeeIcon className="w-7 h-7 text-sage-dark mb-2" data-id="element-119" />
          <div className="text-sm font-bold text-text-dark mb-1" data-id="element-120">
            Bitter Ritual
          </div>
          <div className="text-xs text-text-light" data-id="element-121">30-second guide</div>
        </div>
        <div className="bg-white rounded-2xl p-4 cursor-pointer transition-transform active:scale-[0.97] shadow-[0_2px_12px_rgba(0,0,0,0.06)]" onClick={() => openModal('lymph')} data-id="element-122">
          <SparklesIcon className="w-7 h-7 text-teal mb-2" data-id="element-123" />
          <div className="text-sm font-bold text-text-dark mb-1" data-id="element-124">
            3-Point Press
          </div>
          <div className="text-xs text-text-light" data-id="element-125">2-min technique</div>
        </div>
        <div className="bg-white rounded-2xl p-4 cursor-pointer transition-transform active:scale-[0.97] shadow-[0_2px_12px_rgba(0,0,0,0.06)]" onClick={() => navTo('tracker')} data-id="element-126">
          <BarChartIcon className="w-7 h-7 text-gold mb-2" data-id="element-127" />
          <div className="text-sm font-bold text-text-dark mb-1" data-id="element-128">Log Today</div>
          <div className="text-xs text-text-light" data-id="element-129">Track symptoms</div>
        </div>
        <div className="bg-white rounded-2xl p-4 cursor-pointer transition-transform active:scale-[0.97] shadow-[0_2px_12px_rgba(0,0,0,0.06)]" onClick={() => navTo('tools')} data-id="element-130">
          <WrenchIcon className="w-7 h-7 text-text-mid mb-2" data-id="element-131" />
          <div className="text-sm font-bold text-text-dark mb-1" data-id="element-132">
            Tools & Meals
          </div>
          <div className="text-xs text-text-light" data-id="element-133">Recipes & more</div>
        </div>
      </div>

      {/* Recipes */}
      <div className="px-5 pt-2 pb-2.5 flex items-center justify-between" data-id="element-134">
        <div className="text-lg font-bold text-text-dark" data-id="element-135">
          Drainage Meals Today
        </div>
        <div className="text-[13px] text-sage-dark font-semibold cursor-pointer" onClick={() => navTo('tools')} data-id="element-136">
          View All
        </div>
      </div>

      <div className="px-5 flex gap-3 overflow-x-auto hide-scrollbar pb-4" data-id="element-137">
        <div className="min-w-[160px] bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer shrink-0" onClick={() => openModal('recipe1')} data-id="element-138">
          <div className="h-[100px] bg-gradient-to-br from-[#E8F4EA] to-[#C8E6CC] flex items-center justify-center" data-id="element-139">
            <SaladIcon className="w-12 h-12 text-sage-dark" data-id="element-140" />
          </div>
          <div className="p-3" data-id="element-141">
            <div className="text-sm font-bold text-text-dark" data-id="element-142">
              Liver Greens Bowl
            </div>
            <div className="flex gap-1 flex-wrap mt-1.5" data-id="element-143">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-144">
                Lunch
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-145">
                Anti-bloat
              </span>
            </div>
          </div>
        </div>

        <div className="min-w-[160px] bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer shrink-0" onClick={() => openModal('recipe2')} data-id="element-146">
          <div className="h-[100px] bg-gradient-to-br from-[#FFF0E8] to-[#FFD5B8] flex items-center justify-center" data-id="element-147">
            <CupSodaIcon className="w-12 h-12 text-orange-700" data-id="element-148" />
          </div>
          <div className="p-3" data-id="element-149">
            <div className="text-sm font-bold text-text-dark" data-id="element-150">
              Bitter Ginger Tonic
            </div>
            <div className="flex gap-1 flex-wrap mt-1.5" data-id="element-151">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-152">
                Morning
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-153">
                Liver
              </span>
            </div>
          </div>
        </div>

        <div className="min-w-[160px] bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer shrink-0" onClick={() => openModal('recipe3')} data-id="element-154">
          <div className="h-[100px] bg-gradient-to-br from-[#E8F0FF] to-[#C8D8FF] flex items-center justify-center" data-id="element-155">
            <CarrotIcon className="w-12 h-12 text-blue-700" data-id="element-156" />
          </div>
          <div className="p-3" data-id="element-157">
            <div className="text-sm font-bold text-text-dark" data-id="element-158">
              Sulfur Detox Plate
            </div>
            <div className="flex gap-1 flex-wrap mt-1.5" data-id="element-159">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-160">
                Dinner
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-161">
                Phase II
              </span>
            </div>
          </div>
        </div>

        <div className="min-w-[160px] bg-white rounded-2xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.06)] cursor-pointer shrink-0" onClick={() => openModal('mag')} data-id="element-162">
          <div className="h-[100px] bg-gradient-to-br from-[#FFF4D0] to-[#FFE098] flex items-center justify-center" data-id="element-163">
            <BeakerIcon className="w-12 h-12 text-yellow-700" data-id="element-164" />
          </div>
          <div className="p-3" data-id="element-165">
            <div className="text-sm font-bold text-text-dark" data-id="element-166">
              Mag-Potassium
            </div>
            <div className="flex gap-1 flex-wrap mt-1.5" data-id="element-167">
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-168">
                Evening
              </span>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-cream text-text-mid font-semibold" data-id="element-169">
                Minerals
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>;
}